/**
 * Attorney Coverage — the state -> attorney routing table (slice 1 of attorney question
 * routing). See docs/DESIGN_ATTORNEY_QUESTION_ROUTING.md
 *
 * SHIPS DARK. Nothing routes or emails off this table yet. It exists first so coverage is
 * correct and reviewed before the routing engine dispatches its first member question — a
 * state with no active attorney becomes an exception at 06:00, so the gap is worth finding
 * here rather than in a batch run.
 *
 * Read is admin/superadmin/manager; write is admin/superadmin. The route guard mirrors that,
 * but the RPCs enforce it — a manager who reaches the write controls still gets refused
 * server-side, so the disabled buttons below are an affordance, not the security boundary.
 */

import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AttorneyRecord from "../../components/attorney/AttorneyRecord";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import { featureFlags } from "../../utils/featureFlags";
import Button from "../../components/common/Button";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import {
  listAttorneyCoverage,
  listAttorneyCandidates,
  upsertAttorneyCoverage,
  setAttorneyCoverageActive,
  US_STATES,
  type AttorneyCoverageRow,
} from "../../services/attorneyCoverage.service";

const WRITE_ROLES = ["admin", "superadmin"];

interface FormState {
  id: string | null;
  state_code: string;
  attorney_id: string;
  is_backup: boolean;
  effective_from: string;
  effective_to: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  id: null,
  state_code: "",
  attorney_id: "",
  is_backup: false,
  effective_from: "",
  effective_to: "",
  notes: "",
};

/**
 * Dark-flag gate. Returns BEFORE any query is declared, so a flagged-off environment issues
 * no RPCs at all rather than rendering an empty shell that still talks to the database.
 */
function FeatureOffNotice() {
  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
        <strong className="block text-gray-900 mb-1">Not available yet</strong>
        Attorney question routing is still being built and is not switched on in
        this environment.
      </div>
    </div>
  );
}

/** Build the coverage CSV. Pure and exported so the escaping can be tested without standing
 *  up an attorney profile, coverage rows and a browser — QA has zero attorney profiles, so a
 *  UI-driven test would only ever have exercised the empty case.
 *
 *  `usable` is the SERVER's verdict, not a recomputation: a row can be active and still not
 *  usable because its effective-date window has closed or the person is no longer an attorney.
 *  Exporting our own guess at that is how the four divergent definitions in audit H4 happened.
 */
export function buildCoverageCsv(
  rows: AttorneyCoverageRow[],
  uncoveredStates: string[],
): string {
  const header = [
    "state_code",
    "attorney_name",
    "attorney_email",
    "tier",
    "active",
    "usable_for_routing",
    "effective_from",
    "effective_to",
    "notes",
    "last_updated",
  ];
  const csvCell = (v: unknown) => {
    const t = v === null || v === undefined ? "" : String(v);
    // A leading =, +, - or @ makes Excel treat the cell as a formula. Neutralise it — this
    // file is emailed around, which is exactly where that bites.
    const safe = /^[=+\-@]/.test(t) ? `'${t}` : t;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  return [
    header.join(","),
    ...[...rows]
      .sort((a, b) =>
        a.state_code === b.state_code
          ? Number(a.is_backup) - Number(b.is_backup)
          : a.state_code.localeCompare(b.state_code),
      )
      .map((r) =>
        [
          r.state_code,
          r.attorney_name,
          r.attorney_email,
          r.is_backup ? "backup" : "primary",
          r.active ? "yes" : "no",
          r.usable ? "yes" : "no",
          r.effective_from,
          r.effective_to,
          r.notes,
          r.updated_at,
        ]
          .map(csvCell)
          .join(","),
      ),
    // Uncovered states ride along in the same file. Sending a coverage list that silently
    // omits the states with nobody on them is the failure this export exists to prevent.
    ...(uncoveredStates.length
      ? [
          "",
          csvCell(`UNCOVERED — no usable primary (${uncoveredStates.length})`),
          ...uncoveredStates.map((code) => csvCell(code)),
        ]
      : []),
  ].join("\n");
}

const AttorneyCoverageInner: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  // Matches RequireRole's normalization so "super_admin" and "superadmin" behave alike.
  const canWrite = WRITE_ROLES.includes(
    (user?.role ?? "").toLowerCase().replace(/_/g, ""),
  );

  const coverageQ = useQuery({
    queryKey: ["attorney-coverage", includeInactive],
    queryFn: () => listAttorneyCoverage(includeInactive),
  });
  const candidatesQ = useQuery({
    queryKey: ["attorney-candidates"],
    queryFn: listAttorneyCandidates,
  });

  const rows: AttorneyCoverageRow[] = coverageQ.data ?? [];
  const candidates = candidatesQ.data ?? [];

  // Which states have no usable PRIMARY. This is the number that matters: at dispatch time a
  // state with no active primary produces an exception instead of an answer.
  //
  // `usable` is the SERVER's verdict (attorney_usable_coverage) and the UI must not recompute
  // it. This block used to test `r.active && !r.is_backup`, which silently ignored the
  // effective-date window and could not see an assignee who had been deactivated or was no
  // longer an attorney — one of the four divergent definitions audit H4 was about.
  //
  // 2026-09-21: the owner decided the CRM becomes the source of truth for coverage and the
  // shared attorney workbook is exported from it or retired. A COUNT was enough while the
  // workbook was authoritative and someone could eyeball it; once it is gone, "3 states
  // uncovered" tells Harrison nothing he can act on. List them.
  const uncoveredStates = useMemo(() => {
    const covered = new Set(
      rows.filter((r) => r.usable).map((r) => r.state_code),
    );
    // `.code` is the same accessor the state dropdown below uses — keep them identical so a
    // shape change breaks both together rather than silently emptying this list.
    return US_STATES.map((s) => s.code).filter((code) => !covered.has(code));
  }, [rows]);
  const uncoveredCount = uncoveredStates.length;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["attorney-coverage"] });
  };

  // The workbook is being retired in favour of this table, so this page has to be able to
  // hand back what the workbook gave people: a flat list they can read, sort and circulate.
  // Exports exactly the rows on screen — including the Show-inactive toggle — so what you
  // send is what you were looking at.
  //
  // `usable` is the SERVER's verdict, not a recomputation: a row can be active and still not
  // usable because its effective-date window has closed or the person is no longer an
  // attorney. Exporting our own guess at that is how the four divergent definitions in audit
  // H4 happened.
  // The workbook is being retired in favour of this table, so this page has to hand back what
  // the workbook gave people: a flat list they can read, sort and circulate. Exports exactly
  // the rows on screen — including the Show-inactive toggle — so what you send is what you
  // were looking at.
  const handleExport = () => {
    const blob = new Blob([buildCoverageCsv(rows, uncoveredStates)], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attorney-coverage-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    if (!form) {
      return;
    }
    if (!form.state_code || !form.attorney_id) {
      showToast("error", "Choose a state and an attorney.");
      return;
    }
    setSaving(true);
    try {
      await upsertAttorneyCoverage({
        id: form.id,
        state_code: form.state_code,
        attorney_id: form.attorney_id,
        is_backup: form.is_backup,
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
        notes: form.notes || null,
      });
      showToast("success", form.id ? "Coverage updated." : "Coverage added.");
      setForm(null);
      refresh();
    } catch (e) {
      showToast("error", (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (row: AttorneyCoverageRow) => {
    try {
      const result = await setAttorneyCoverageActive(row.id, !row.active);
      if (result?.warning) {
        // Legitimate action, consequential result: the operator gets to know the state is
        // now unroutable rather than discovering it as a batch exception later.
        showToast("warning", result.warning);
      } else {
        showToast(
          "success",
          row.active ? "Coverage deactivated." : "Coverage reactivated.",
        );
      }
      refresh();
    } catch (e) {
      showToast("error", (e as Error).message);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Attorney Coverage
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Who answers member questions from each state. This is the record —
            maintain it here, not in a spreadsheet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/*
            The header's "Export CSV" and "Add coverage" belonged to the ROUTING PROJECTION
            table, which is now collapsed below and is empty. Leaving them here put a
            permanently-disabled Export button at the top of the page and an "Add coverage"
            that writes to a table nobody reads yet — two dead controls above the record that
            actually matters. The record has its own export, beside the data it exports.
          */}
        </div>
      </div>

      {/* Say plainly that nothing is dispatched yet, so nobody assumes questions are flowing. */}
      <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <strong>Nothing is emailed yet.</strong> This record is correct and
        maintained here, but the routing engine that dispatches questions has
        not shipped. Keeping it right now means the first member question goes
        to the right attorney.
      </div>

      {/* THE RECORD — loaded from Harrison's workbook 2026-09-21 and maintained here since. */}
      <AttorneyRecord canWrite={canWrite} />

      {/*
        The login-attorney coverage table lives below, collapsed. Since migs 898/907 routing
        reads the RECORD above (every listed attorney receives the question); this table only
        adds attorneys with CRM logins (none yet). Showing it open alongside the record invites the two-sources-of-truth confusion
        this page exists to end.
      */}
      <details className="mt-10 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <summary className="cursor-pointer text-sm font-medium text-gray-700">
          Login-attorney coverage (advanced) — overrides for attorneys with CRM
          logins
        </summary>
        <p className="mt-2 text-xs text-gray-500">
          Questions are routed from the attorney record above: every attorney
          listed for a state or region receives the question (co-counsel
          included). This table is only for attorneys who have a CRM login (none
          yet). A primary added here receives questions for that whole state
          alongside the record&apos;s attorneys; a backup here is used only when
          the state has no other attorney. It cannot hold the Texas or North
          Carolina regions.
        </p>
        {/*
          These two act on the PROJECTION, not on the record, so they live inside the
          disclosure. At the top of the page the export sat permanently disabled (the table is
          empty) and "Add coverage" wrote to a table nobody reads yet — dead controls above the
          data that matters. Kept rather than deleted: slice 3 will need them.
        */}
        <div className="mt-3 flex items-center gap-2">
          <Button
            onClick={handleExport}
            variant="secondary"
            disabled={!rows.length}
          >
            Export CSV
          </Button>
          {canWrite && (
            <Button
              onClick={() => setForm({ ...EMPTY_FORM })}
              variant="primary"
            >
              Add coverage
            </Button>
          )}
        </div>
      </details>
      <div className="hidden">
        {coverageQ.error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {(coverageQ.error as Error).message}
          </div>
        )}

        {candidatesQ.data && candidates.length === 0 && (
          <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
            <strong>No attorney accounts yet.</strong> Coverage can only point
            at a user who has the <em>Attorney</em> role. Create those accounts
            first, then assign states here.
          </div>
        )}

        <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
          <div className="text-sm text-gray-600">
            {
              new Set(rows.filter((r) => r.usable).map((r) => r.state_code))
                .size
            }{" "}
            states with a usable primary
            {uncoveredCount > 0 && (
              <span className="ml-2 text-yellow-700">
                · {uncoveredCount} state{uncoveredCount === 1 ? "" : "s"} still
                uncovered
              </span>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show inactive
          </label>
        </div>

        {/* Name them. With the workbook retired this page is the only place anyone can find out
          WHICH state has nobody on it, and a bare count is not actionable. */}
        {uncoveredCount > 0 && (
          <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 p-3">
            <p className="text-sm font-medium text-yellow-900 mb-1">
              No usable primary attorney in {uncoveredCount} state
              {uncoveredCount === 1 ? "" : "s"}
            </p>
            <p className="text-xs text-yellow-800 mb-2">
              A member question from one of these produces an exception rather
              than an answer.
            </p>
            <div className="flex flex-wrap gap-1">
              {uncoveredStates.map((code) => (
                <span
                  key={code}
                  className="inline-block rounded bg-yellow-100 px-1.5 py-0.5 text-xs font-mono text-yellow-900"
                >
                  {code}
                </span>
              ))}
            </div>
          </div>
        )}

        {form && canWrite && (
          <div className="mb-6 rounded-lg border border-gray-300 bg-white p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {form.id ? "Edit coverage" : "Add coverage"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="ac-state"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  State
                </label>
                <select
                  id="ac-state"
                  value={form.state_code}
                  onChange={(e) =>
                    setForm({ ...form, state_code: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md text-sm py-2 px-2 bg-white"
                >
                  <option value="">Select a state…</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="ac-attorney"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Attorney
                </label>
                <select
                  id="ac-attorney"
                  value={form.attorney_id}
                  onChange={(e) =>
                    setForm({ ...form, attorney_id: e.target.value })
                  }
                  disabled={candidates.length === 0}
                  className="w-full border border-gray-300 rounded-md text-sm py-2 px-2 bg-white disabled:bg-gray-100"
                >
                  <option value="">
                    {candidates.length === 0
                      ? "No attorney accounts"
                      : "Select an attorney…"}
                  </option>
                  {candidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name || c.email || c.id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="ac-role"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Role for this state
                </label>
                <select
                  id="ac-role"
                  value={form.is_backup ? "backup" : "primary"}
                  onChange={(e) =>
                    setForm({ ...form, is_backup: e.target.value === "backup" })
                  }
                  className="w-full border border-gray-300 rounded-md text-sm py-2 px-2 bg-white"
                >
                  <option value="primary">
                    Primary — receives the question
                  </option>
                  <option value="backup">
                    Backup — used when no primary is active
                  </option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="ac-from"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Effective from{" "}
                  <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="ac-from"
                  type="date"
                  value={form.effective_from}
                  onChange={(e) =>
                    setForm({ ...form, effective_from: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
                />
              </div>
              <div>
                <label
                  htmlFor="ac-to"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Effective to <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="ac-to"
                  type="date"
                  value={form.effective_to}
                  onChange={(e) =>
                    setForm({ ...form, effective_to: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
                />
              </div>
              <div>
                <label
                  htmlFor="ac-notes"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Notes <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="ac-notes"
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
                />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={handleSave} variant="primary" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                onClick={() => setForm(null)}
                variant="secondary"
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {coverageQ.isLoading ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
            No attorney coverage configured yet. Every state needs an active
            primary attorney before questions can be routed.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    State
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Attorney
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Role
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Effective
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                    Status
                  </th>
                  {canWrite && <th className="px-4 py-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={r.active ? "" : "bg-gray-50 text-gray-500"}
                  >
                    <td className="px-4 py-2 font-medium">{r.state_code}</td>
                    <td className="px-4 py-2">
                      <div>{r.attorney_name || "—"}</div>
                      <div className="text-xs text-gray-400">
                        {r.attorney_email}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs ${
                          r.is_backup
                            ? "bg-gray-100 text-gray-700"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {r.is_backup ? "Backup" : "Primary"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {r.effective_from || "—"} → {r.effective_to || "open"}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs ${
                          r.active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {r.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          className="text-blue-600 hover:underline mr-3"
                          onClick={() =>
                            setForm({
                              id: r.id,
                              state_code: r.state_code,
                              attorney_id: r.attorney_id,
                              is_backup: r.is_backup,
                              effective_from: r.effective_from ?? "",
                              effective_to: r.effective_to ?? "",
                              notes: r.notes ?? "",
                            })
                          }
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-gray-600 hover:underline"
                          onClick={() => handleToggleActive(r)}
                        >
                          {r.active ? "Deactivate" : "Reactivate"}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const AttorneyCoverage: React.FC = () =>
  featureFlags.attorneyQuestionRouting ? (
    <AttorneyCoverageInner />
  ) : (
    <FeatureOffNotice />
  );

export default AttorneyCoverage;
